import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { EntreeInventoryV1, toStockDateParam } from '@producers/entree-inventory/entree-inventory';
import { InventoryStockCell, StockDayCell } from '@producers/inventory-stock-cell/inventory-stock-cell';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';

import { PageHeader } from '@core/components/page-header/page-header';
import { ImageFallbackDirective } from '@core/directives/image-fallback';
import { ServiceResponseV1 } from '@core/models/apis';
import { EntreeV1, MenuItemByProducerQuery, MenuItemStatus } from '@core/models/menu-item-pending';
import { AddressV1, EstablishmentV1, ProducerStatus, ProducerV1 } from '@core/models/producer-admin';
import { ProducerOnboardingV1 } from '@core/models/producer-onboarding';
import { EntreeInventoryService } from '@core/services/entree-inventory';
import { MenuItemsService } from '@core/services/menu-items';
import { ProducersService } from '@core/services/producers';

import { ChangeStatusDialog } from '../change-status-dialog/change-status-dialog';
import { ProducerOnboardingReview } from '../producer-onboarding-review/producer-onboarding-review';

/** Short weekday labels for the stock-column day headers, indexed by `Date.getDay()`. */
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** How many days the at-a-glance stock column covers: today and the next two. */
const STOCK_WINDOW_DAYS = 3;

const DEFAULT_PAGE_SIZE = 25;
const DETAILS_TAB = 'details';
const MENU_ITEMS_TAB = 'menu-items';
const ONBOARDING_TAB = 'onboarding';

interface StatusOption {
  label: string;
  value: MenuItemStatus;
}

@Component({
  selector: 'app-producer-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CardModule,
    MessageModule,
    SelectModule,
    SkeletonModule,
    TableModule,
    TabsModule,
    TagModule,
    PageHeader,
    ImageFallbackDirective,
    ChangeStatusDialog,
    InventoryStockCell,
    ProducerOnboardingReview
  ],
  templateUrl: './producer-details.html',
  styleUrl: './producer-details.scss'
})
export class ProducerDetails {
  private readonly router = inject(Router);
  private readonly producersAPIs = inject(ProducersService);
  private readonly menuItems = inject(MenuItemsService);
  private readonly entreeInventory = inject(EntreeInventoryService);

  readonly id = input.required<string>();
  readonly tab = input<string | undefined>(undefined);

  readonly producer = signal<ProducerV1 | null>(null);
  readonly loading = signal<boolean>(false);
  readonly error = signal<boolean>(false);

  readonly activeTab = signal<string>(DETAILS_TAB);
  readonly menuItemsPage = signal<ServiceResponseV1<EntreeV1[]> | null>(null);
  readonly menuItemsLoading = signal<boolean>(false);
  readonly menuItemsError = signal<boolean>(false);
  readonly menuItemsLoaded = signal<boolean>(false);
  readonly menuItemsPageNum = signal<number>(1);
  readonly menuItemsPageSize = signal<number>(DEFAULT_PAGE_SIZE);
  readonly selectedStatus = signal<MenuItemStatus | null>(null);

  readonly onboarding = signal<ProducerOnboardingV1 | null>(null);
  readonly onboardingLoading = signal<boolean>(false);
  readonly onboardingError = signal<boolean>(false);

  readonly statusDialogVisible = signal<boolean>(false);
  readonly statusSubmitting = signal<boolean>(false);
  readonly statusUpdateError = signal<boolean>(false);

  readonly statusOptions: StatusOption[] = [
    { label: 'Pending submission', value: MenuItemStatus.PendingSubmission },
    { label: 'Pending approval', value: MenuItemStatus.PendingApproval },
    { label: 'Approved', value: MenuItemStatus.Approved },
    { label: 'Rejected', value: MenuItemStatus.Rejected },
    { label: 'Completed', value: MenuItemStatus.Completed }
  ];

  readonly DETAILS_TAB = DETAILS_TAB;
  readonly MENU_ITEMS_TAB = MENU_ITEMS_TAB;
  readonly ONBOARDING_TAB = ONBOARDING_TAB;

  readonly menuItemRows = computed<EntreeV1[]>(() => this.menuItemsPage()?.data ?? []);
  readonly menuItemsTotalRecords = computed<number>(() => this.menuItemsPage()?.paging?.totalSize ?? 0);

  readonly selectedEstablishmentId = signal<string | null>(null);
  private readonly inventoryRows = signal<EntreeInventoryV1[]>([]);

  readonly establishments = computed<EstablishmentV1[]>(() => this.producer()?.establishments ?? []);

  /** Today and the next two days as `YYYY-MM-DD` plus a short weekday label, built from local dates. */
  private readonly stockWindow = ((): { stockDate: string; label: string }[] => {
    const today = new Date();
    const days: { stockDate: string; label: string }[] = [];
    for (let offset = 0; offset < STOCK_WINDOW_DAYS; offset++) {
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
      days.push({ stockDate: toStockDateParam(date), label: WEEKDAY_LABELS[date.getDay()] });
    }
    return days;
  })();

  /** Header label for the stock column, e.g. `Stock — Tue · Wed · Thu`. */
  readonly stockColumnHeader = `Stock — ${this.stockWindow.map((d) => d.label).join(' · ')}`;

  /** Inventory rows keyed by lower-cased entree id, then by stock date, for a per-item day lookup. */
  private readonly inventoryByEntree = computed<Map<string, Map<string, EntreeInventoryV1>>>(() => {
    const byEntree = new Map<string, Map<string, EntreeInventoryV1>>();
    for (const row of this.inventoryRows()) {
      const key = row.entreeId.toLowerCase();
      const byDate = byEntree.get(key) ?? new Map<string, EntreeInventoryV1>();
      byDate.set(row.stockDate, row);
      byEntree.set(key, byDate);
    }
    return byEntree;
  });

  /**
   * The three-day stock cells for one menu item, or `null` when the item is not offered (only approved
   * and completed items hold dated stock). An offered item with no row for a day shows that day as blank.
   */
  stockCellsFor(item: EntreeV1): StockDayCell[] | null {
    const offered = item.status === MenuItemStatus.Approved || item.status === MenuItemStatus.Completed;
    if (!offered) {
      return null;
    }
    const byDate = this.inventoryByEntree().get(item.id.toLowerCase());
    return this.stockWindow.map((day) => {
      const row = byDate?.get(day.stockDate);
      return {
        label: day.label,
        stock: row?.stock ?? null,
        capacity: row ? row.stock + row.held + row.committed : null
      };
    });
  }

  constructor() {
    effect(() => {
      const tab = this.tab();
      if (tab === MENU_ITEMS_TAB || tab === ONBOARDING_TAB) {
        untracked(() => this.activeTab.set(tab));
      }
    });

    effect(() => {
      const producerId = this.id();
      if (!producerId) {
        return;
      }
      untracked(() => void this.loadProducer(producerId));
    });

    effect(() => {
      const producerId = this.id();
      const tab = this.activeTab();
      const query: MenuItemByProducerQuery = {
        pageNum: this.menuItemsPageNum(),
        pageSize: this.menuItemsPageSize(),
        sort: 'name:asc',
        status: this.selectedStatus() ?? undefined
      };
      if (!producerId || tab !== MENU_ITEMS_TAB) {
        return;
      }
      untracked(() => void this.loadMenuItems(producerId, query));
    });

    effect(() => {
      const producerId = this.id();
      const onOnboardingTab = this.activeTab() === ONBOARDING_TAB;
      if (!producerId || !onOnboardingTab) {
        return;
      }
      untracked(() => void this.loadOnboarding(producerId));
    });

    // Default the stock establishment to the first the producer holds, re-defaulting if the list
    // changes to one that no longer contains the current selection.
    effect(() => {
      const list = this.establishments();
      untracked(() => {
        const current = this.selectedEstablishmentId();
        if (!current || !list.some((e) => e.id === current)) {
          this.selectedEstablishmentId.set(list[0]?.id ?? null);
        }
      });
    });

    // Load the three-day stock window for the chosen establishment, once the menu items tab is open.
    effect(() => {
      const establishmentId = this.selectedEstablishmentId();
      const onMenuItemsTab = this.activeTab() === MENU_ITEMS_TAB;
      untracked(() => {
        if (onMenuItemsTab && establishmentId) {
          void this.loadInventory(establishmentId);
        } else {
          this.inventoryRows.set([]);
        }
      });
    });
  }

  readonly statusSeverity = computed<'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'>(() => {
    switch (this.producer()?.status) {
      case ProducerStatus.Active:
        return 'success';
      case ProducerStatus.Ready:
        return 'info';
      case ProducerStatus.Pending:
        return 'warn';
      case ProducerStatus.Lead:
        return 'secondary';
      case ProducerStatus.Suspended:
        return 'danger';
      case ProducerStatus.Deactivated:
        return 'contrast';
      default:
        return 'secondary';
    }
  });

  /** One-line postal format; returns '' when no address is on record so the template can hide the row. */
  formatAddress(address: AddressV1 | null | undefined): string {
    if (!address) {
      return '';
    }
    const lineTwo = address.addressLineTwo ? `, ${address.addressLineTwo}` : '';
    return `${address.addressLineOne}${lineTwo}, ${address.city}, ${address.state} ${address.zip}`;
  }

  getMenuItemStatusSeverity(
    status: MenuItemStatus | null
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case MenuItemStatus.Approved:
        return 'success';
      case MenuItemStatus.Rejected:
        return 'danger';
      case MenuItemStatus.PendingApproval:
        return 'warn';
      case MenuItemStatus.Completed:
        return 'info';
      default:
        return 'secondary';
    }
  }

  onTabChange(value: string | number | undefined): void {
    if (value === undefined) {
      return;
    }
    this.activeTab.set(String(value));
  }

  onStatusFilterChange(): void {
    this.menuItemsPageNum.set(1);
  }

  onMenuItemsPage(event: { first: number; rows: number }): void {
    this.menuItemsPageNum.set(Math.floor(event.first / event.rows) + 1);
    this.menuItemsPageSize.set(event.rows);
  }

  openMenuItem(item: EntreeV1): void {
    this.router.navigate(['/menu-items', item.id], {
      queryParams: { from: `producer/${this.id()}` }
    });
  }

  backToList(): void {
    this.router.navigate(['/producers']);
  }

  openStatusDialog(): void {
    this.statusUpdateError.set(false);
    this.statusDialogVisible.set(true);
  }

  async changeStatus(status: ProducerStatus): Promise<void> {
    const producer = this.producer();
    if (!producer) {
      return;
    }

    this.statusSubmitting.set(true);
    this.statusUpdateError.set(false);

    try {
      const updated = await firstValueFrom(this.producersAPIs.updateProducerStatus(producer.id, status));
      this.producer.set(updated);
      this.statusDialogVisible.set(false);
    } catch {
      this.statusUpdateError.set(true);
    } finally {
      this.statusSubmitting.set(false);
    }
  }

  private async loadProducer(producerId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(false);

    try {
      const detail = await firstValueFrom(this.producersAPIs.getProducerDetail(producerId));
      this.producer.set(detail);
    } catch {
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private async loadMenuItems(producerId: string, query: MenuItemByProducerQuery): Promise<void> {
    this.menuItemsLoading.set(true);
    this.menuItemsError.set(false);

    try {
      const response = await firstValueFrom(this.menuItems.getMenuItemsByProducer(producerId, query));
      this.menuItemsPage.set(response);
    } catch {
      this.menuItemsError.set(true);
    } finally {
      this.menuItemsLoading.set(false);
      this.menuItemsLoaded.set(true);
    }
  }

  private async loadOnboarding(producerId: string): Promise<void> {
    this.onboardingLoading.set(true);
    this.onboardingError.set(false);

    try {
      const onboarding = await firstValueFrom(this.producersAPIs.getProducerOnboarding(producerId));
      this.onboarding.set(onboarding);
    } catch {
      this.onboardingError.set(true);
    } finally {
      this.onboardingLoading.set(false);
    }
  }

  /**
   * Reads every entree's stock at the establishment across the three-day window in one request (no
   * entree filter), for the at-a-glance stock column. A failure just leaves the column blank rather than
   * surfacing an error the operator cannot act on from this list.
   */
  private async loadInventory(establishmentId: string): Promise<void> {
    try {
      const rows = await firstValueFrom(
        this.entreeInventory.list({
          establishmentId,
          fromDate: this.stockWindow[0].stockDate,
          toDate: this.stockWindow[this.stockWindow.length - 1].stockDate
        })
      );
      this.inventoryRows.set(rows);
    } catch {
      this.inventoryRows.set([]);
    }
  }
}
